import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, Paginator, fmtDate, ExportButton } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataActionDialog } from "@/lib/bulk";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", "SUSPENDED", "all"];

const DELIVERY_KEYS = [["self_delivery_enabled", "Own Shop Delivery"], ["pickup_enabled", "Self Pickup"], ["sonu_delivery_enabled", "Sonu Delivery Partner"]];

function DeliveryToggles({ shop, onDone }) {
  const ds = shop.delivery_settings || {};
  const val = { self_delivery_enabled: !!ds.self_delivery_enabled, pickup_enabled: ds.pickup_enabled !== false, sonu_delivery_enabled: ds.sonu_delivery_enabled !== false };
  const flip = async (k) => {
    try { await api.put(`/admin/shops/${shop.id}/delivery-options`, { [k]: !val[k] }); toast.success("Delivery options updated"); onDone(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="text-xs mt-2 flex flex-wrap gap-1.5" data-testid={`shop-delivery-options-${shop.id}`}>
      {DELIVERY_KEYS.map(([k, l]) => (
        <button key={k} onClick={() => flip(k)} data-testid={`shop-${k}-${shop.id}`}
          className={`px-2 py-0.5 rounded border transition-colors ${val[k] ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
          {l}: {val[k] ? "On" : "Off"}</button>))}
      {!Object.values(val).some(Boolean) && <span className="text-red-600">No order option available</span>}
    </div>
  );
}


export default function Shops() {
  const [status, setStatus] = useState("PENDING");
  const [page, setPage] = useState(1);
  const limit = 20;
  const path = `/admin/shops?page=${page}&limit=${limit}${status !== "all" ? `&status=${status}` : ""}`;
  const { data, loading, error, refetch } = useFetch(path, [path]);
  const [modal, setModal] = useState(null); // {shop, action}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null); // "approve" | "reject"
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { setSelected(new Set()); }, [status, page]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = data?.items?.length && data.items.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(data.items.map((s) => s.id)));

  const act = async () => {
    setBusy(true);
    try {
      const newStatus = modal.action === "approve" ? "APPROVED" : modal.action === "reject" ? "REJECTED" : "SUSPENDED";
      await api.post(`/admin/shops/${modal.shop.id}/approval`, { status: newStatus, reason: reason || null });
      toast.success(`Shop ${newStatus.toLowerCase()}`);
      setModal(null); setReason(""); refetch();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const runBulk = async () => {
    setBulkBusy(true);
    const newStatus = bulk === "approve" ? "APPROVED" : "REJECTED";
    const ids = Array.from(selected);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/admin/shops/${id}/approval`, { status: newStatus, reason: reason || null }); ok += 1; }
      catch { fail += 1; }
    }
    setBulkBusy(false); setBulk(null); setReason(""); setSelected(new Set());
    toast[fail ? "warning" : "success"](`${ok} shop(s) ${newStatus.toLowerCase()}${fail ? `, ${fail} failed` : ""}`);
    refetch();
  };

  const [archiveOpen, setArchiveOpen] = useState(false);

  const columns = [
    { key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "address", label: "Address" },
    { key: "pincode", label: "Pincode" }, { key: "city", label: "City" }, { key: "status", label: "Status" },
    { key: "gstin", label: "GSTIN", get: (r) => r.gst?.gstin }, { key: "created_at", label: "Registered" },
  ];

  return (
    <div>
      <PageHeader title="Shops Management" subtitle="Review, approve and manage shopkeeper stores"
        actions={<ExportButton filename="sonu-shops.csv" rows={data?.items} columns={columns} testid="export-shops-btn" />} />
      <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} className="mb-4">
        <TabsList data-testid="shops-status-tabs">
          {STATUSES.map((s) => <TabsTrigger key={s} value={s} data-testid={`shops-tab-${s}`}>{s === "all" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {status === "PENDING" && data?.items?.length > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 rounded-lg border bg-card" data-testid="bulk-bar">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={!!allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="bulk-select-all" />
            Select all
          </label>
          <span className="text-sm text-muted-foreground" data-testid="bulk-count">{selected.size} selected</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" disabled={!selected.size} onClick={() => setBulk("approve")} data-testid="bulk-approve-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve selected</Button>
            <Button size="sm" variant="destructive" disabled={!selected.size} onClick={() => setBulk("reject")} data-testid="bulk-reject-btn"><XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject selected</Button>
            <Button size="sm" variant="destructive" disabled={!selected.size || bulkBusy} onClick={() => setArchiveOpen(true)} data-testid="bulk-delete-shops-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Archive selected</Button>
          </div>
          <DataActionDialog entity="shops" ids={Array.from(selected)} open={archiveOpen} onClose={() => setArchiveOpen(false)}
            onDone={() => { setSelected(new Set()); refetch(); }} />
        </div>
      )}

      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
        !data?.items?.length ? <SectionCard className="p-2"><EmptyState label={`No ${status.toLowerCase()} shops`} /></SectionCard> : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="shops-table">
            {data.items.map((s) => (
              <SectionCard key={s.id} className="p-4 flex flex-col" data-testid={`shop-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    {s.status === "PENDING" && (
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSel(s.id)} className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" data-testid={`shop-select-${s.id}`} />
                    )}
                    <div>
                      <div className="font-semibold font-outfit">{s.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {s.phone}</div>
                    </div>
                  </div>
                  <StatusChip status={s.status} />
                </div>
                <div className="text-xs text-muted-foreground flex items-start gap-1 mt-2">
                  <MapPin className="h-3 w-3 mt-0.5 shrink-0" /> <span>{s.address}, {s.pincode} · {s.city || "Lucknow"}</span>
                </div>
                <div className="text-xs mt-2 flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 rounded bg-muted">GST: {s.gst?.registered ? s.gst?.gstin || "Yes" : "Unregistered"}</span>
                </div>
                <DeliveryToggles shop={s} onDone={refetch} />
                <div className="text-[11px] text-muted-foreground mt-2">Registered {fmtDate(s.created_at)}</div>
                <div className="mt-3 pt-3 border-t flex gap-2">
                  {s.status === "PENDING" && (
                    <>
                      <Button size="sm" className="flex-1" onClick={() => setModal({ shop: s, action: "approve" })} data-testid={`shop-approve-btn-${s.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve</Button>
                      <Button size="sm" variant="destructive" className="flex-1" onClick={() => setModal({ shop: s, action: "reject" })} data-testid={`shop-reject-btn-${s.id}`}><XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject</Button>
                    </>
                  )}
                  {s.status === "APPROVED" && (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setModal({ shop: s, action: "suspend" })} data-testid={`shop-suspend-btn-${s.id}`}>Suspend</Button>
                  )}
                  {(s.status === "REJECTED" || s.status === "SUSPENDED") && (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setModal({ shop: s, action: "approve" })} data-testid={`shop-reactivate-btn-${s.id}`}>Approve</Button>
                  )}
                </div>
              </SectionCard>
            ))}
          </div>
        )}
      <Paginator page={page} total={data?.total} limit={limit} onPage={setPage} />

      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) { setModal(null); setReason(""); } }}>
        <DialogContent data-testid="shop-action-dialog">
          <DialogHeader><DialogTitle className="capitalize">{modal?.action} “{modal?.shop?.name}”</DialogTitle></DialogHeader>
          <div>
            <label className="text-sm font-medium">Reason / note {modal?.action !== "approve" && <span className="text-muted-foreground">(recommended)</span>}</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Add a note for the shopkeeper…" className="mt-1.5" data-testid="shop-action-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setModal(null); setReason(""); }}>Cancel</Button>
            <Button onClick={act} disabled={busy} variant={modal?.action === "approve" ? "default" : "destructive"} data-testid="shop-action-confirm">{busy ? "Saving…" : `Confirm ${modal?.action}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bulk} onOpenChange={(o) => { if (!o) { setBulk(null); setReason(""); } }}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader><DialogTitle className="capitalize">Bulk {bulk} {selected.size} shop(s)?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will {bulk} all selected pending shops. This action notifies each shopkeeper.</p>
          <div>
            <label className="text-sm font-medium">Reason / note (optional)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Applied to all selected shops…" className="mt-1.5" data-testid="bulk-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulk(null); setReason(""); }}>Cancel</Button>
            <Button onClick={runBulk} disabled={bulkBusy} variant={bulk === "approve" ? "default" : "destructive"} data-testid="bulk-confirm-btn">{bulkBusy ? "Processing…" : `Confirm bulk ${bulk}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
